<template>
    <e-mobile-modal v-model="visibleComputed" class="map-mobile-search-modal" position="top" hide-close
        :fullscreen="isMobileLayoutOrPreview">

        <template #header>
            <div class="w-100" :class="{ 'pb-4': isDesktopDevice }">
                <e-search-input v-model="search" autofocus size="lg" type="search" class="select-location-input" @change="onSearched"
                    :placeholder="targetType === 'start' ? $t('map.trajectory.selectStartPoint') : $t('map.trajectory.chooseDestination')">

                    <template #append>
                        <b-btn variant="text" pill class="px-3 py-2 active-scale"
                            @click="visibleComputed = false" size="xl">
                            <i class="fas fa-arrow-left" />
                        </b-btn>
                    </template>
                </e-search-input>
            </div>
        </template>

        <b-list-group flush class="select-location-list map-mobile-list-group">
            <template v-if="search?.length >= 2">
                <b-list-group-item v-for="item in searchResults" :key="`modal-${targetType}-search-${item.id}`"
                                   class="select-location-item interactive active-scale"
                                   @click="searchItemClicked(item); selectCmsItem(item); ">
                    <div class="list-group-item-icon">
                        <i :class="[fontAwesomePrefix, item.glyph || 'fa-location-dot']" />
                    </div>
                    <div class="list-group-item-content">
                        <span>{{ $tr(item.getName()) }}</span>
                        <div v-if="item.floor" class="text-muted">{{ $tr(item.floor.getName()) }}</div>
                        <b-badge v-if="item.getMatchingKeywords(searchRegexp)" variant="light" class="md keyword" pill>
                            <i class="fa-search text-muted" :class="fontAwesomePrefix" style="margin-left: -3px"/> {{item.getMatchingKeywords(searchRegexp)}}
                        </b-badge>
                    </div>
                </b-list-group-item>
                <b-list-group-item v-if="searchResults?.length === 0" class="mx-auto pb-5">
                    <i>{{ $t('map.trajectory.noSearchResult') }}</i>
                </b-list-group-item>
            </template>
            <template v-else>
                <div class="list-group-shortcuts">
                    <b-list-group-item v-if="canSelectCurrentLocation" @click="selectMyCurrentLocation" class="interactive active-scale position-relative highlighted-item">
                        <div class="list-group-item-icon">
                            <i :class="[fontAwesomePrefix, 'fa-location-crosshairs']" />
                        </div>
                        <div class="list-group-item-content">
                            <!-- <span>{{ $t('map.myCurrentPosition') }}</span> -->
                            <div class="list-group-item-title">{{ $t('map.findMe') }}</div>
                            <div class="list-group-item-description">{{ $t('map.useGps') }}</div>
                        </div>
                        <div v-if="startFromCurrentLocationLoading || startFromCurrentLocationDisabled" class="loading-location-container">
                            <div v-if="startFromCurrentLocationLoading" style="width: 24px; height: 24px;">
                                <i class="fas fa-spinner-third fa-spin fa-2x"/>
                            </div>
                            <div v-if="startFromCurrentLocationDisabled">
                                <b-badge variant="danger" pill class="text-white">{{ $t('map.startFromCurrentLocationDisabled') }}</b-badge>
                            </div>
                        </div>
                    </b-list-group-item>
                    <b-list-group-item v-if="canSelectFromMap" @click="selectFromMap" class="interactive active-scale">
                        <div class="list-group-item-icon">
                            <i :class="[fontAwesomePrefix, 'fa-map-location-dot']" />
                        </div>
                        <div class="list-group-item-content">
                            <!-- <span>{{ $t('map.trajectory.selectOnMap') }}</span> -->
                            <div class="list-group-item-title">{{ $t('map.common.select') }}</div>
                            <div class="list-group-item-description">{{ $t('map.trajectory.fromMap') }}</div>
                        </div>
                    </b-list-group-item>
                    <b-list-group-item v-if="isQrCodeEnabled && targetType === 'start'" @click="$emit('open-qr-scanner')" class="interactive active-scale">
                        <div class="list-group-item-icon">
                            <i :class="[fontAwesomePrefix, 'fa-qrcode-read']" />
                        </div>
                        <div class="list-group-item-content">
                            <!-- <span>{{ $t('map.assistant.qrCodeScanner.open') }}</span> -->
                            <div class="list-group-item-title">{{ $t('map.assistant.qrCodeScanner.scan') }}</div>
                            <div class="list-group-item-description">{{ $t('map.assistant.qrCodeScanner.qrCode') }}</div>
                        </div>
                    </b-list-group-item>
                </div>
                <template v-if="quickOptions.length">
                    <div class="list-group-item-section">{{ $t('map.recentLocation') }}</div>
                    <b-list-group-item v-for="option in quickOptions" :key="`modal-${targetType}-quick-${option.type}`" class="interactive active-scale"
                                       @click="selectQuickOption(option.type)">
                        <div class="list-group-item-icon">
                            <i :class="[fontAwesomePrefix, option.glyph || 'fa-clock']" />
                        </div>
                        {{ option.label }}
                    </b-list-group-item>
                </template>
                <template v-if="entrances.length">
                    <div class="list-group-item-section">{{ $t('map.trajectory.entrancesExits') }}</div>
                    <b-list-group-item v-for="item in entrances" :key="`modal-${targetType}-entr-${item.id}`" class="interactive active-scale"
                                       @click="selectCmsItem(item)">
                        <div class="list-group-item-icon">
                            <i :class="[fontAwesomePrefix, item.glyph || 'fa-door-open']" />
                        </div>
                        <div class="list-group-item-content">
                            {{ $tr(item.getName()) }}
                            <div v-if="item.floor" class="text-muted">{{ $tr(item.floor.getName()) }}</div>
                        </div>
                    </b-list-group-item>
                </template>
                <template v-if="autoComplete.length">
                    <div class="list-group-item-section">{{ $t('map.trajectory.locations') }}</div>
                    <b-list-group-item v-for="item in autoComplete" :key="`modal-${targetType}-auto-${item.id}`" class="interactive active-scale"
                                       @click="selectCmsItem(item)">
                        <div class="list-group-item-icon">
                            <i :class="[fontAwesomePrefix, item.glyph || 'fa-location-dot']" />
                        </div>
                        <div class="list-group-item-content">
                            {{ $tr(item.getName()) }}
                            <div v-if="item.floor" class="text-muted">{{ $tr(item.floor.getName()) }}</div>
                        </div>
                    </b-list-group-item>
                </template>
            </template>
        </b-list-group>
    </e-mobile-modal>
</template>

<script>
import EDrawer from "@/components/e-drawer.vue";
import EQrCode from "@vue-components/components/e-qr-code.vue";
import ESearchInput from "@vue-components/components/e-search-input.vue";
import EMobileModal from "@/components/e-mobile-modal.vue";
import EToast from "@/components/e-toast.vue";
import MapMixin from "@/helpers/maps/map-mixin.js";
import CmsAnalyticsSearch from "../../../../common/db/models/cms/CmsAnalyticsSearch.js";
import SessionTracker from "@/helpers/SessionTracker.js";
import NetworkV2 from "@vue-components/helpers/NetworkV2.js";

export default {
    name: `map-mobile-search-modal`,
    components: { ESearchInput, EDrawer, EQrCode, EMobileModal, EToast },
    mixins: [MapMixin],
    props: {
        visible: { type: Boolean, default: false },
        initialSearch: {type: String, default: null},
        targetType: {type: String, default: `start`},
        currentLocation: { type: Object },
        endPoint: { type: Object },
        navPath: { type: Object },
        pathMode: { type: String, default: `fastest` },
        latestStartPoint: { type: Object, default: null },
        latestDestinationPoint: { type: Object, default: null },
        startFromCurrentLocation: { type: Boolean, default: false },
        startFromCurrentLocationLoading: { type: Boolean, default: false },
        startFromCurrentLocationDisabled: { type: Boolean, default: false },
    },
    data() {
        return {
            search: ``,
            analyticsSearch: null
        };
    },
    computed: {
        visibleComputed: {
            get() {
                return this.visible;
            },
            set(value) {
                this.$emit(`update:visible`, value);
            }
        },
        entrances() {
            if (!this.currentMap) {
                return [];
            }
            return this.reduceMapItems(this.currentMap.items.filter((i) => i.visibility !== `never` && i.type === `entrance` && i.cmsItem));
        },
        autoComplete() {
            if (!this.currentMap) {
                return [];
            }
            return this.reduceMapItems(this.currentMap.items.filter((i) => i.visibility !== `never` && i.type !== `entrance` && i.cmsItem));
        },
        searchRegexp() {
            const normalizedSearch = this.search.normalize(`NFKD`).replace(/\W/g, ``);
            return new RegExp(normalizedSearch, `gi`);
        },
        searchResults() {
            if (this.search.length < 2) {
                return null;
            } else {
                const list = this.currentMap.items.filter((i) => i.visibility !== `never`);
                return this.reduceMapItems(list.filter(l => l.matchSearch(this.searchRegexp)));
            }
        },
        canSelectCurrentLocation() {
            return this.targetType === `start` && this.mapIntegrations.isGpsEnabled() && !this.isDesktopDevice;
        },
        canSelectFromMap() {
            return (this.targetType === `start` || this.targetType === `end`) && !!this.currentMap;
        },
        quickOptions() {
            const options = [];
            if (this.currentLocation && this.targetType === `start`) {
                options.push({
                    type: `current-location`,
                    icon: `fa-location-crosshairs`,
                    label: this.$t(`map.youAreHere`)
                });
            }
            if (this.latestDestinationPoint) {
                options.push({
                    type: `latest-destination`,
                    label: this.latestDestinationPoint.getFullName()
                });
            }
            if (this.latestStartPoint) {
                options.push({
                    type: `latest-start`,
                    label: this.latestStartPoint.getFullName()
                });
            }
            return options;
        }
    },
    mounted() {
        if (this.initialSearch) {
            this.search = this.initialSearch;
        }
    },
    methods: {
        closeSelectModal() {
            this.visibleComputed = false;
        },
        selectCmsItem(cmsItem) {
            this.$emit(`endpoint-selected`, {
                target: this.targetType,
                type: `cms-item`,
                cmsItemId: cmsItem?.id || null
            });
            this.closeSelectModal();
        },
        selectQuickOption(type) {
            this.$emit(`endpoint-selected`, {
                target: this.targetType,
                type
            });
            this.closeSelectModal();
        },
        selectMyCurrentLocation() {
            if (this.startFromCurrentLocationLoading) {
                return;
            }

            if (!this.canSelectCurrentLocation) {
                return;
            }

            this.$emit(`endpoint-selected`, {
                target: `start`,
                type: `current-location`
            });
            this.closeSelectModal();
        },
        selectFromMap() {
            if (!this.canSelectFromMap) {
                return;
            }
            this.$emit(`select-from-map`, this.targetType);
            this.closeSelectModal();
        },
        reduceMapItems(mapItems) {
            const items = [...new Set(mapItems.map((mapItem) => mapItem.cmsItem).filter(Boolean))];
            items.sort((a, b) => this.$tr(a.getName()).localeCompare(this.$tr(b.getName())));
            return items;
        },
        onSearched() {
            if (this.search) {
                this.analyticsSearch = new CmsAnalyticsSearch({
                    sessionId: SessionTracker.session.id,
                    websiteId: this.cmsWebsite.id,
                    dealerid: this.dealerid,
                    search: this.search,
                    results: this.searchResults.length
                });

                this.sendAnalytics();
            }
        },
        searchItemClicked(item) {
            if (item && this.analyticsSearch) {
                this.analyticsSearch.item = {
                    id: item.id,
                    name: item.name,
                    type: item.type
                };
                const keywords = item.getMatchingKeywords(this.searchRegexp, false);
                if (keywords) {
                    this.analyticsSearch.keywords = keywords;
                }

                if (this.analyticsSearch.id && this.analyticsSearch.search === this.search) {
                    // add to existing analytics search
                    NetworkV2.put(`/api/public/analytics/searches/item-clicked`, this.analyticsSearch);
                } else {
                    this.sendAnalytics();
                }
            }
        },
        sendAnalytics() {
            // use debounce to handle situation when we click on item while still having focus on search to send one request
            if (this.analyticsDebounce) {
                clearTimeout(this.analyticsDebounce);
            }
            this.analyticsDebounce = setTimeout(() => {
                NetworkV2.post(`/api/public/analytics/searches/add`, this.analyticsSearch)
                    .then(resp => {
                        this.analyticsSearch.id = resp.data.id;
                    });
            }, 500);
        },
    },
};
</script>

<style lang="scss" scoped>
@import "./map-mobile-list-group.scss";

.map-mobile-search-modal {
    :deep(.e-mobile-modal__body) {
        padding: 0;
    }

    .modal-title {
        font-size: 14pt;
        font-weight: 700;
        margin-bottom: 10px;
    }

    .list-group-item-section {
        font-size: 12pt;
        font-weight: 600;
        background: var(--color-light);

        position: sticky;
        top: 0;
        z-index: 1;

        padding: 5px 10px;
        margin: 6px 12px 4px;
        background-color: color-mix(in srgb, var(--color-accent) 10%, white);
        border-radius: 12px;
        border: none;
        text-align: center;

        display: flex;
        justify-content: center;

        box-shadow: inset 0 0 0 1px rgba(0, 95, 229, 0.04);
    }

    .loading-location-container {
        position: absolute;
        top: 0;
        right: 0;
        left:  0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.12);
        display: flex;
        backdrop-filter: blur(3px);
        align-items: center;
        pointer-events: none;
        justify-content: center;
    }

    .list-group-item.highlighted-item {
        background: linear-gradient(135deg, rgba(0, 95, 229, 0.12), rgba(255, 255, 255, 0.96));
        border-color: rgba(0, 95, 229, 0.14);
        box-shadow: inset 0 0 0 1px rgba(0, 95, 229, 0.04);
    }

    .list-group-shortcuts {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 6px;
        padding: 0 10px;
        text-align: center;

        .list-group-item + .list-group-item {
            border-left: 1px solid rgba(0, 0, 0, 0.06) !important;
        }

        .list-group-item {
            box-shadow: none !important;
            border-top-width: 1px !important;

            display: flex;
            flex-direction: column;
            gap: 4px;
            align-items: center;
            padding: 6px;
            border-radius: 0;
            line-height: 1.3;
            border: none !important;

            &.highlighted-item {
                background: none !important;
                border-radius: 8px;

                .list-group-item-icon {
                    background: var(--color-accent);
                    color: white;
                    border-radius: 10px !important;
                }
            }

            .list-group-item-icon {
                border-radius: 10px !important;
            }


            transition: transform 150ms ease, box-shadow 150ms ease;

            &:active,
            &:hover {
                transform: translateY(-1px);
                --tw-shadow: 0 1px 2px 0 #0000001a, 0 2px 4px -1px #0000001a;
            }

            .list-group-item-content {
                display: flex;
                flex-direction: column;
                align-items: center;
            }

            .list-group-item-title {
                font-size: 11pt;
                font-weight: 500;
                color: var(--color-accent);
                text-align: center;
            }

            .list-group-item-description {
                font-size: 10pt;
                color: var(--color-muted);
                text-align: center;
            }
        }

        .list-group-item-icon {
            width: 34px;
            height: 34px;
            // border-radius: 8px;
            // background: rgba(0, 95, 229, 0.12);
            background-color: color-mix(in srgb, var(--color-accent) 10%, white);
            color: var(--color-accent);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
        }
    }

    .highlighted-item .list-group-item-icon {
        background: rgba(0, 95, 229, 0.16);
        color: var(--color-accent);
        box-shadow: inset 0 0 0 1px rgba(0, 95, 229, 0.08);
    }
    :deep(.select-location-input) {
        .form-control {
            background: var(--color-light);
            border-radius: 999px;
            border: none;
            padding-left: 50px;
            padding-right: 40px;
        }

        .input-append {
            opacity: 1;
            top: 50%;
            bottom: 0;
            left: 12px;
            height: fit-content;

            transform: translateY(-50%);
        }

        .clear-icon {
            right: 12px;
        }
    }
}
</style>



